import { gsap, ScrollTrigger } from '../utils/gsap-config.js';
import DeviceDetector from '../utils/device-detector.js';
import ScrollDataAnimation from '../scroll-data-animation.js';

class AnimationManager {
  constructor() {
    this.scrollTriggers = [];
    this.deviceDetector = DeviceDetector.getInstance();
    this.isInitialized = false;
    this.scrollDataAnimation = null;
    
    this.init();
  }

  init() {
    this.deviceDetector.onDesktop(() => {
      this.initDesktopAnimations();
    });

    this.deviceDetector.onMobile(() => {
      this.destroyDesktopAnimations();
      this.initMobileStaticState();
    });
  }

  async initDesktopAnimations() {
    if (this.isInitialized) return;
    
    await this.delay(100);
    
    this.initScrollAnimations();
    
    this.isInitialized = true;
  }

  destroyDesktopAnimations() {
    if (!this.isInitialized) return;
    
    this.scrollTriggers.forEach(trigger => trigger.kill());
    this.scrollTriggers = [];
    
    gsap.killTweensOf("*");

    if (this.scrollDataAnimation) {
      this.scrollDataAnimation.destroy();
      this.scrollDataAnimation = null;
    }
    
    this.resetElementStyles();
    
    this.isInitialized = false;
  }



  initScrollAnimations() {
    // this.initFadeInAnimations();
    // this.initImageAnimations();
    // this.initCounterAnimations();
    // this.initParallaxAnimations();

    this.initTreeContainerAnimation();
    this.initScrollDataAnimations();
  }

  initMobileStaticState() {
    document.documentElement.classList.add('mobile-static');
    
    this.applyMobileFinalStates();
  }

  initFadeInAnimations() {
    const fadeElements = document.querySelectorAll('[data-animate="fade-in"]');
    
    fadeElements.forEach(element => {
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: "top 80%",
        end: "bottom 20%",
        onEnter: () => {
          gsap.fromTo(element, 
            { opacity: 0, y: 60 },
            { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
          );
        },
        onLeave: () => {
          gsap.to(element, { opacity: 0.7, y: -20, duration: 0.5 });
        },
        onEnterBack: () => {
          gsap.to(element, { opacity: 1, y: 0, duration: 0.5 });
        },
      });
      
      this.scrollTriggers.push(trigger);
    });
  }

  initImageAnimations() {
    const imageElements = document.querySelectorAll('[data-animate="image-scale"]');
    
    imageElements.forEach(element => {
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: "top 80%",
        onEnter: () => {
          gsap.fromTo(element,
            { scale: 0.8, opacity: 0 },
            { scale: 1, opacity: 1, duration: 1.2, ease: "power2.out" }
          );
        }
      });
      
      this.scrollTriggers.push(trigger);
    });
  }

  initCounterAnimations() {
    const counterElements = document.querySelectorAll('[data-animate="counter"]');
    
    counterElements.forEach(element => {
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: "top 80%",
        onEnter: () => {
          const text = element.textContent;
          const matches = text.match(/[\d,]+/);
          if (matches) {
            const endValue = parseInt(matches[0].replace(/,/g, ''));
            gsap.fromTo({ value: 0 }, 
              { value: endValue, duration: 2, ease: "power2.out" },
              {
                onUpdate: function() {
                  const currentValue = Math.floor(this.targets()[0].value);
                  element.textContent = text.replace(/[\d,]+/, currentValue.toLocaleString());
                }
              }
            );
          }
        }
      });
      
      this.scrollTriggers.push(trigger);
    });
  }

  initParallaxAnimations() {
    const parallaxElements = document.querySelectorAll('[data-animate="parallax"]');
    
    parallaxElements.forEach(element => {
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
        onUpdate: (self) => {
          const progress = self.progress;
          const yPos = progress * -50;
          gsap.set(element, { y: yPos });
        }
      });
      
      this.scrollTriggers.push(trigger);
    });
  }

  initTreeContainerAnimation() {
    const treeContainer = document.getElementById('tree-container');
    
    if (!treeContainer) return;

    const tl = gsap.timeline();
    
    // Initial state - hidden at bottom
    gsap.set(treeContainer, { 
      opacity: 0, 
      y: 50,
      scale: 0.95
    });

    // Entrance animation
    tl.to(treeContainer, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 1.5,
      ease: "power2.out",
      delay: 0.5
    });

    // Floating animation - starts after entrance
    tl.to(treeContainer, {
      y: -20,
      duration: 3,
      ease: "power1.inOut",
      repeat: -1,
      yoyo: true,
      repeatDelay: 0
    }, "+=0.5");

  }

  initScrollDataAnimations() {
    if (!this.scrollDataAnimation) {
      this.scrollDataAnimation = new ScrollDataAnimation();
    }
  }

  applyMobileFinalStates() {
    const animatedElements = document.querySelectorAll('[data-animate]');
    animatedElements.forEach(element => {
      const animationType = element.dataset.animate;
      
      switch (animationType) {
        case 'fade-in':
        case 'image-scale':
          gsap.set(element, { opacity: 1, scale: 1, y: 0, x: 0 });
          break;
        case 'counter':
          // Counter values are already final in HTML
          break;
        case 'parallax':
          gsap.set(element, { y: 0 });
          break;
      }
    });

    // Set tree-container to final visible state on mobile
    const treeContainer = document.getElementById('tree-container');
    if (treeContainer) {
      gsap.set(treeContainer, { 
        opacity: 1, 
        y: 0, 
        scale: 1, 
        rotation: 0 
      });
    }
  }

  resetElementStyles() {
    const animatedElements = document.querySelectorAll('[data-animate]');
    animatedElements.forEach(element => {
      element.style.cssText = '';
    });

    // Reset tree-container styles
    const treeContainer = document.getElementById('tree-container');
    if (treeContainer) {
      treeContainer.style.cssText = '';
    }
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  scrollTo(target, options = {}) {
    document.querySelector(target)?.scrollIntoView({ 
      behavior: 'smooth',
      ...options 
    });
  }

  pauseAnimations() {
    // Pause any running animations if needed
  }

  resumeAnimations() {
    // Resume animations if needed
  }
}

export default AnimationManager; 